import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const NotesModalContext = createContext();

const STORAGE_KEY = 'persistentNotesModal';
// NotesModalProvider is mounted above <Router> (unconditionally, including
// while logged out), so it never remounts on logout -- its React state
// persists in memory across the auth boundary in the same tab. Clearing
// localStorage alone isn't enough: without also resetting this live state,
// the previous user's ticker/notes (and an open modal) would carry straight
// over to whichever user logs in next on this browser. App.js's handleLogout
// can't call useNotesModal() directly (it renders NotesModalProvider, so
// it's the provider's parent, not a descendant), so it dispatches this
// window event instead -- the same cross-tree-boundary pattern already used
// by strategyPresetModalManager.js.
export const CLEAR_NOTES_MODAL_EVENT = 'notesModal:clear';

// Helper to load state from localStorage
const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        isVisible: parsed.isVisible || false,
        ticker: parsed.ticker || null,
        notes: parsed.notes || [],
      };
    }
  } catch (error) {
    console.error('Failed to load notes modal state from localStorage:', error);
  }
  return {
    isVisible: false,
    ticker: null,
    notes: [],
  };
};

// Helper to save state to localStorage
const saveToStorage = (isVisible, ticker, notes) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      isVisible,
      ticker,
      notes,
    }));
  } catch (error) {
    console.error('Failed to save notes modal state to localStorage:', error);
  }
};

export const useNotesModal = () => {
  const context = useContext(NotesModalContext);
  if (!context) {
    throw new Error('useNotesModal must be used within a NotesModalProvider');
  }
  return context;
};

export const NotesModalProvider = ({ children }) => {
  // Initialize state from localStorage
  const initialState = loadFromStorage();
  const [isVisible, setIsVisible] = useState(initialState.isVisible);
  const [ticker, setTicker] = useState(initialState.ticker);
  const [notes, setNotes] = useState(initialState.notes);

  // Save to localStorage whenever state changes
  useEffect(() => {
    saveToStorage(isVisible, ticker, notes);
  }, [isVisible, ticker, notes]);

  const showNotesModal = useCallback((tickerData, tickerNotes = []) => {
    setTicker(tickerData);
    setNotes(tickerNotes);
    setIsVisible(true);
  }, []);

  const hideNotesModal = useCallback(() => {
    setIsVisible(false);
    // Keep ticker and notes data so modal can be reopened to same state
  }, []);

  const updateNotes = useCallback((newNotes) => {
    setNotes(newNotes);
  }, []);

  const clearNotesModal = useCallback(() => {
    setIsVisible(false);
    setTicker(null);
    setNotes([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Listen for logout (dispatched from App.js, which can't reach this
  // context directly -- see CLEAR_NOTES_MODAL_EVENT above) and clear both
  // the in-memory state and localStorage, so the next user to log in on
  // this browser doesn't inherit the previous user's open modal or notes.
  useEffect(() => {
    window.addEventListener(CLEAR_NOTES_MODAL_EVENT, clearNotesModal);
    return () => window.removeEventListener(CLEAR_NOTES_MODAL_EVENT, clearNotesModal);
  }, [clearNotesModal]);

  const value = {
    isVisible,
    ticker,
    notes,
    showNotesModal,
    hideNotesModal,
    updateNotes,
    clearNotesModal,
  };

  return (
    <NotesModalContext.Provider value={value}>
      {children}
    </NotesModalContext.Provider>
  );
};
